/**
 * UI-STABILITY-1: per-user table state memory.
 *
 * - localStorage  → persistent filters / sort.
 * - sessionStorage → ephemeral search text.
 *
 * Keys are scoped per user.id (with email fallback) so different users on
 * the same browser cannot inherit each other's state.
 *
 * All read paths are defensive: any corrupted / wrong-version blob is
 * silently dropped and the caller falls back to defaults.
 */

const VERSION = 1;

const userKey = (user) => {
  if (!user) return null;
  return user.id || user.email || null;
};

const baseKey = (user, table, kind) => {
  const u = userKey(user);
  if (!u) return null;
  return `crm:${u}:${table}:${kind}`;
};

/* ---------- localStorage (filters + sort) -------------------------------- */

export function loadPrefs(user, table) {
  const k = baseKey(user, table, "listPrefs");
  if (!k || typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(k);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || typeof data !== "object" || data.version !== VERSION) {
      window.localStorage.removeItem(k);
      return null;
    }
    return data;
  } catch {
    try { window.localStorage.removeItem(k); } catch (e) { void e; }
    return null;
  }
}

export function savePrefs(user, table, payload) {
  const k = baseKey(user, table, "listPrefs");
  if (!k || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      k,
      JSON.stringify({ version: VERSION, ...payload })
    );
  } catch (e) { void e; }
}

export function clearPrefs(user, table) {
  const k = baseKey(user, table, "listPrefs");
  if (!k || typeof window === "undefined") return;
  try { window.localStorage.removeItem(k); } catch (e) { void e; }
}

/* ---------- sessionStorage (search text) --------------------------------- */

export function loadSession(user, table) {
  const k = baseKey(user, table, "sessionState");
  if (!k || typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(k);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || typeof data !== "object" || data.version !== VERSION) {
      window.sessionStorage.removeItem(k);
      return null;
    }
    return data;
  } catch {
    try { window.sessionStorage.removeItem(k); } catch (e) { void e; }
    return null;
  }
}

export function saveSession(user, table, payload) {
  const k = baseKey(user, table, "sessionState");
  if (!k || typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(
      k,
      JSON.stringify({ version: VERSION, ...payload })
    );
  } catch (e) { void e; }
}

export function clearSession(user, table) {
  const k = baseKey(user, table, "sessionState");
  if (!k || typeof window === "undefined") return;
  try { window.sessionStorage.removeItem(k); } catch (e) { void e; }
}

/* ---------- logout-wide CRM session cleanup ----------------------------- */

/**
 * UI-STABILITY-1B: remove ALL CRM table sessionStorage keys (search text)
 * regardless of which user they belong to. Called from central logout.
 *
 * Strict pattern match: `crm:<userId>:<table>:sessionState`. localStorage
 * listPrefs entries are intentionally left untouched so persistent
 * filter/sort preferences survive logout/login on the same browser.
 */
export function clearAllTableSessionState() {
  if (typeof window === "undefined") return;
  try {
    const store = window.sessionStorage;
    const toRemove = [];
    for (let i = 0; i < store.length; i++) {
      const key = store.key(i);
      if (
        key &&
        key.startsWith("crm:") &&
        key.endsWith(":sessionState")
      ) {
        toRemove.push(key);
      }
    }
    toRemove.forEach((k) => store.removeItem(k));
  } catch (e) { void e; }
}
